import { computed, inject, Injectable, Signal } from '@angular/core';
import { TenderItemAttributesResponseItemPurchaser } from 'net-api-lib';
import { ChoicesService } from '../choices.service';
import { AmountChoice } from '../models';

interface ItemBase {
  itemPurchasers?: TenderItemAttributesResponseItemPurchaser[] | null;
  amountTypeId?: number | null;
  description?: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class TenderItemsService {
  private readonly choicesService = inject(ChoicesService);

  getItemsSignal<TItems extends ItemBase>(
    searchValue: Signal<string>,
    allItems: Signal<TItems[]>
  ) {
    return computed(() => {
      const search = searchValue().toLowerCase();
      const tenderItems = allItems();
      const amountChoicesValue = this.choicesService.amountChoices();

      if (!tenderItems || !amountChoicesValue) return [];

      const itemsExtended = this.extendItems(tenderItems, amountChoicesValue);

      return this.filterItems(search, itemsExtended);
    });
  }

  // Extend tender-items with additional properties
  private extendItems<TItems extends ItemBase>(
    tenderItems: TItems[],
    amountChoices: AmountChoice[]
  ) {
    return tenderItems.map(item => ({
      ...item,
      amount: item.itemPurchasers?.[0]?.amount ?? undefined,
      amountUnit: amountChoices.find(
        choice => choice.value === item.amountTypeId
      )?.label,
      searchText: item.description!.toLowerCase(),
    }));
  }

  private filterItems<TItems extends { searchText: string }>(
    search: string | null,
    items: TItems[]
  ) {
    if (!search) return items;

    const words = search.split(' ');

    return items.filter(item =>
      words.every(word => item.searchText.includes(word))
    );
  }
}
